import type { ReactNode } from "react";
import { ShieldAlert, LogOut } from "lucide-react";
import { useSuperAdminAuth } from "./SuperAdminAuthProvider";
import { SuperAdminLogin } from "./SuperAdminLogin";
import { useT } from "../i18n";

/**
 * Gate-component. Состояния:
 *   • loading → spinner
 *   • anonymous / magicLinkSent / error → SuperAdminLogin
 *   • authenticated:
 *      - app_metadata.role === 'super_admin' → children
 *      - else → "Доступ запрещён"
 *
 * Role в JWT проставляется DB-trigger `set_super_admin_role` после
 * matching email в `super_admin_allowlist`. Если ты только что добавил
 * себя в allowlist — нужно один раз logout + login, чтобы trigger
 * сработал на свежий auth.users.UPDATE.
 */
export function SuperAdminGate({ children }: { children: ReactNode }) {
  const { status, signOut } = useSuperAdminAuth();
  const t = useT();

  if (status.kind === "loading" || status.kind === "idle") {
    return (
      <div
        className="min-h-dvh grid place-items-center"
        style={{ background: "var(--bg)", color: "var(--fg-soft)" }}
      >
        <div className="flex items-center gap-2 text-sm">
          <span className="spinner" />
          {t.gate.loading}
        </div>
      </div>
    );
  }

  if (
    status.kind === "anonymous" ||
    status.kind === "magicLinkSent" ||
    status.kind === "error"
  ) {
    return <SuperAdminLogin />;
  }

  // authenticated — проверяем role
  const meta = status.user.app_metadata as Record<string, unknown> | undefined;
  const role = (meta?.role as string | undefined) ?? "";

  if (role !== "super_admin") {
    return (
      <div
        className="min-h-dvh grid place-items-center px-4"
        style={{ background: "var(--bg)" }}
      >
        <div
          className="w-full max-w-md p-7 text-center"
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--warn)",
            borderRadius: "var(--radius-lg)",
            boxShadow: "var(--shadow-lg)",
          }}
        >
          <ShieldAlert
            className="mx-auto mb-3 h-12 w-12"
            style={{ color: "var(--warn)" }}
            aria-hidden
          />
          <h2 className="mb-2 text-xl font-extrabold" style={{ color: "var(--fg)" }}>
            {t.gate.denied}
          </h2>
          <p className="mb-1 text-sm" style={{ color: "var(--fg-soft)" }}>
            {t.gate.deniedHint}
          </p>
          <p
            className="mb-5 text-xs font-mono"
            style={{ color: "var(--fg-muted)" }}
          >
            {status.user.email}
          </p>
          <button
            type="button"
            onClick={signOut}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-extrabold uppercase tracking-wide"
            style={{
              background: "var(--bg-muted)",
              color: "var(--fg)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-md)",
            }}
          >
            <LogOut className="h-4 w-4" aria-hidden />
            {t.gate.signOut}
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
